// The live stream for one document: other devices' operations, and later the
// advisor's notes, comments and runs. EventSource reconnects on its own; on
// every (re)connection the server states its revision, and if ours is behind
// the caller catches up by replaying the operations it missed.

import type { Op } from "../store/ops";

export type DocumentEvent =
  | { type: "open"; rev: number }
  | { type: "ops"; rev: number; client_id: string; ops: Op[] }
  | { type: "document"; rev: number }
  // A version was put back (design §13). The prose changed without an
  // operation, so there is nothing to catch up on: every copy takes the
  // document again.
  | { type: "restored"; rev: number }
  | { type: "versions" }
  // The advisor changed something: a Review committed, a comment was
  // dismissed elsewhere. The payload is deliberately just the news; the
  // client refetches, because the state is small and changes rarely.
  | { type: "advisor" }
  | { type: "run"; run_id: string; status: string; error?: unknown }
  | { type: "run_step"; run_id: string; step: string; detail: string }
  | { type: "run_usage"; run_id: string; output_tokens: number };

export function connectEvents(
  documentId: string,
  onEvent: (event: DocumentEvent) => void,
  onStateChange: (open: boolean) => void,
): () => void {
  const source = new EventSource(`/api/documents/${documentId}/events`, {
    withCredentials: true,
  });
  source.onopen = () => onStateChange(true);
  source.onerror = () => onStateChange(false);
  source.onmessage = (message) => {
    try {
      onEvent(JSON.parse(message.data) as DocumentEvent);
    } catch {
      // A malformed frame is ignored; the next revision check reconciles.
    }
  };
  return () => source.close();
}
